/**
 * The searchable shortcut list (Requirement 3.7).
 *
 * Generated from {@link BINDINGS} — the same table the dispatcher reads. Nothing here
 * knows a single key combination by name, which is the point: the published list and
 * the behaviour cannot disagree, because there is only one list.
 *
 * A search box filters through `searchBindings`, so the same query matches a
 * combination (`Ctrl+D`), a description ("fill down"), or a category ("Clipboard").
 */

import { searchBindings, type KeyBinding } from '../grid/shortcuts.js';
import { button, el } from './dom.js';

export interface CheatSheet {
  element: HTMLElement;
  /** The search input. */
  search: HTMLInputElement;
  open(): void;
  close(): void;
  isOpen(): boolean;
  /** Applies a query and re-renders. Returns the matching rows. */
  filter(query: string): KeyBinding[];
  /** The combinations currently listed, in display order. */
  rows(): string[];
}

export interface CheatSheetOptions {
  /** Called after the panel closes, so focus can return to the grid. */
  onClose?: () => void;
}

export function createCheatSheet(options: CheatSheetOptions = {}): CheatSheet {
  const search = el('input', {
    className: 'cheat-search',
    testid: 'shortcut-search',
    ariaLabel: 'Search keyboard shortcuts',
    attrs: { type: 'search', placeholder: 'Search shortcuts', autocomplete: 'off' },
  });
  const table = el('ul', {
    className: 'cheat-list',
    testid: 'shortcut-list',
    ariaLabel: 'Keyboard shortcuts',
  });
  const close = button('Close', () => panel.close(), 'shortcut-close');
  const element = el(
    'div',
    {
      className: 'cheat-sheet',
      testid: 'shortcut-panel',
      role: 'dialog',
      ariaLabel: 'Keyboard shortcuts',
      attrs: { hidden: 'hidden', 'aria-modal': 'false' },
    },
    [
      el('div', { className: 'cheat-head' }, [
        el('strong', { text: 'Keyboard shortcuts' }),
        search,
        close,
      ]),
      table,
    ],
  );

  let listed: KeyBinding[] = [];

  function render(query: string): KeyBinding[] {
    listed = searchBindings(query);
    table.textContent = '';
    for (const binding of listed) {
      table.appendChild(
        el('li', { className: 'cheat-row' }, [
          el('kbd', { className: 'cheat-combo', text: binding.combo }),
          el('span', { className: 'cheat-label', text: binding.label }),
          el('span', {
            className: 'cheat-mode',
            text: `${binding.category} · ${binding.modes.join(', ')}`,
          }),
        ]),
      );
    }
    return listed;
  }

  search.addEventListener('input', () => {
    render(search.value);
  });
  search.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    panel.close();
  });

  const panel: CheatSheet = {
    element,
    search,

    open(): void {
      element.hidden = false;
      render(search.value);
      search.focus();
    },

    close(): void {
      options.onClose?.();
      element.hidden = true;
    },

    isOpen(): boolean {
      return !element.hidden;
    },

    filter(query: string): KeyBinding[] {
      search.value = query;
      return render(query);
    },

    rows(): string[] {
      return listed.map((binding) => binding.combo);
    },
  };

  render('');
  return panel;
}
